"use client"

import { useState, useCallback, useRef, memo, useEffect } from "react"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Button from "@mui/material/Button"
import { FilterIcon, X } from "lucide-react"
import { useRouter } from "next/navigation"
import dynamic from "next/dynamic"
import type { FilterField } from "@/app/types/typeFilter"

// Now we can use ssr: false in a Client Component
const AdvancedFilterModal = dynamic(() => import("./feed-filter-modal"), {
  ssr: false,
  loading: () => (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
    >
      <div
        style={{
          backgroundColor: "#121212",
          borderRadius: "8px",
          padding: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            border: "3px solid rgba(255, 255, 255, 0.3)",
            borderTopColor: "#fff",
            animation: "spin 1s linear infinite",
          }}
        />
      </div>
    </div>
  ),
})

interface FeedFilterActiveClientProps {
  validSegments: string[]
  filterFields: FilterField[]
  extendedFilterFields: FilterField[]
  deltaExtendedFields?: FilterField[]
  handleFilterClick?: (segment: string) => void
  perfilsCount?: number
}

// Memoized filter chip with minimal re-renders
const FilterChip = memo(
  ({
    segment,
    description,
    onRemove,
  }: {
    segment: string
    description: string
    onRemove: (segment: string) => void
  }) => {
    const handleDelete = useCallback(() => onRemove(segment), [segment, onRemove])

    return (
      <Chip
        label={description}
        onDelete={handleDelete}
        color="secondary"
        size="small"
        variant="outlined"
        sx={{
          borderRadius: 1,
          height: "32px",
          fontWeight: 500,
          "& .MuiChip-deleteIcon": {
            fontSize: "18px",
          },
        }}
      />
    )
  },
)
FilterChip.displayName = "FilterChip"

export default function FeedFilterActiveClient({
  validSegments,
  filterFields,
  extendedFilterFields,
  deltaExtendedFields,
  handleFilterClick,
  perfilsCount = 0,
}: FeedFilterActiveClientProps) {
  const router = useRouter()
  const [modalOpen, setModalOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const modalDataRef = useRef({ filterFields, deltaExtendedFields })

  // Ensure component is mounted to avoid hydration issues
  useEffect(() => {
    setMounted(true)
  }, [])

  // Update modal data ref when props change
  modalDataRef.current = { filterFields, deltaExtendedFields }

  // Default filter click handler
  const defaultHandleFilterClick = useCallback(
    (segment: string) => {
      const newSegments = validSegments.includes(segment)
        ? validSegments.filter((s) => s !== segment)
        : [...validSegments, segment]

      router.push(newSegments.length ? `/feed/${newSegments.join("/")}` : "/feed")
    },
    [validSegments, router],
  )

  const handleFilterClickFn = handleFilterClick || defaultHandleFilterClick

  const clearFilters = useCallback(() => {
    router.push("/feed")
  }, [router])

  const handleOpenModal = useCallback(() => {
    setModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setModalOpen(false)
  }, [])

  // Create segment to description map for efficient lookups
  const segmentDescriptionMap = useRef(new Map<string, string>())
  const lastExtendedFieldsRef = useRef<FilterField[] | undefined>(undefined)

  // Update map when extended fields change
  if (extendedFilterFields !== lastExtendedFieldsRef.current) {
    segmentDescriptionMap.current.clear()
    for (const field of extendedFilterFields) {
      for (const value of field.values) {
        segmentDescriptionMap.current.set(value.segment, value.caption)
      }
    }
    lastExtendedFieldsRef.current = extendedFilterFields
  }

  // Don't render until mounted to avoid hydration mismatch
//   if (!mounted) {
//     return (
//       <Box sx={{ mb: 2 }}>
//         <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
//           <div style={{ height: "32px", width: "60px", backgroundColor: "#333", borderRadius: "4px" }} />
//           <div style={{ height: "32px", width: "80px", backgroundColor: "#1976d2", borderRadius: "4px" }} />
//         </Box>
//       </Box>
//     )
//   }

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        {/* Count chip */}
        <Chip
          label={perfilsCount}
          variant="outlined"
          sx={{
            borderRadius: 1,
            backgroundColor: "black",
            borderColor: "grey.800",
            height: 32,
          }}
        />

        {/* Filter button */}
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleOpenModal}
          startIcon={<FilterIcon size={16} />}
          sx={{ height: 32 }}
        >
          Filtros
        </Button>

        {/* Active filter chips */}
        {validSegments.map((segment) => (
          <FilterChip
            key={segment}
            segment={segment}
            description={segmentDescriptionMap.current.get(segment) || segment}
            onRemove={handleFilterClickFn}
          />
        ))}

        {/* Clear all button */}
        {validSegments.length > 0 && (
          <Chip
            label={<X size={16} />}
            onClick={clearFilters}
            color="error"
            size="small"
            variant="filled"
            sx={{ borderRadius: 1, height: 32, minWidth: 32, "& .MuiChip-label": { px: 1 } }}
          />
        )}
      </Box>

      {/* Modal */}
      {modalOpen && (
        <AdvancedFilterModal
          open={modalOpen}
          onClose={handleCloseModal}
          fields={modalDataRef.current.filterFields}
          deltaExtendedFields={modalDataRef.current.deltaExtendedFields}
        />
      )}
    </Box>
  )
}
